/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import {
  dismissMaturityWeeklySummary,
  isMaturityWeeklySummaryDismissed,
} from '../application/onboardingUiStorage';
import { useWeeklyHomeSummary } from '../application/useWeeklyHomeSummary';
import { useHomeOnboardingCardsContext } from '../application/HomeOnboardingCardsContext';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useDismissableCard } from './useDismissableCard';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

type Props = {
  userId: string;
  isOwner?: boolean;
  /** False en día 1 fresh (need_*). */
  offerAllowed?: boolean;
};

export function WeeklyReportsCard({ userId, isOwner = true, offerAllowed = true }: Props) {
  const navigation = useNavigation<any>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canReports = useHasBusinessCapability('reports');
  const { visible, onDismiss } = useDismissableCard(
    userId,
    isMaturityWeeklySummaryDismissed,
    dismissMaturityWeeklySummary,
  );
  const gates = useHomeOnboardingCardsContext();
  const blocking = gates?.blocking ?? false;
  const gatesLoading = gates?.loading ?? false;
  const { summary, loading, reload } = useWeeklyHomeSummary(branchId);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload]),
  );

  const goReports = useCallback(() => {
    navigateInShell(navigation, 'Reports');
  }, [navigation]);

  if (!isOwner || !offerAllowed || !canReports || !visible || blocking) return null;
  if (gatesLoading || (loading && !summary)) return null;
  if (!summary) return null;

  const money = formatMoneyCompact(summary.weekTotal);
  let detail = `Vendiste ${money}`;
  if (summary.pctVsPrev != null) {
    const sign = summary.pctVsPrev >= 0 ? '+' : '';
    detail += ` · ${sign}${summary.pctVsPrev}% vs la anterior`;
  } else if (summary.weekSalesCount > 0) {
    detail += ` · ${summary.weekSalesCount} ventas`;
  }

  return (
    <View style={styles.card} testID="home.weekly-summary">
      <View style={styles.header}>
        <Text style={styles.title}>Esta semana</Text>
        <TouchableOpacity
          onPress={() => void onDismiss()}
          accessibilityRole="button"
          accessibilityLabel="Ocultar"
        >
          <Ionicons name="close" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
      <Text style={styles.detail}>{detail}</Text>
      <TouchableOpacity
        onPress={goReports}
        accessibilityRole="button"
        accessibilityLabel="Ver informe"
        testID="home.weekly-summary.cta"
      >
        <Text style={styles.link}>Ver informe ›</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    gap: spacing.xs,
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    paddingRight: spacing.sm,
  },
  detail: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  link: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
    marginTop: spacing.xs,
  },
});
